import { Pressable, View } from 'react-native';
import { RadioButton, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import type { DrivingLimitMode } from '../../../api/apiService';

/**
 * ALL-174: the driver chooses how the 10 h driving limit is counted. Rolling
 * 24 h stays the default and the recommended option; per day counts
 * 00:00-24:00 Europe/Lisbon. The choice only changes the warning, never any
 * record, the 60 h week, the 4-month average or PDF reports.
 */
const OPTIONS: { mode: DrivingLimitMode; key: 'rolling' | 'calendarDay' }[] = [
  { mode: 'rolling_24h', key: 'rolling' },
  { mode: 'calendar_day', key: 'calendarDay' },
];

export const DrivingLimitModeSetting = ({
  mode,
  onChange,
  disabled = false,
}: {
  mode: DrivingLimitMode;
  onChange: (mode: DrivingLimitMode) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <View style={{ gap: 8 }} testID="driving-limit-mode-setting" accessibilityRole="radiogroup">
      <Text variant="titleSmall" style={{ color: theme.colors.onSurface }}>
        {t('workTime.mode.itemTitle')}
      </Text>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
        {t('workTime.mode.intro')}
      </Text>
      {OPTIONS.map(({ mode: option, key }) => {
        const checked = option === mode;
        return (
          <Pressable
            key={option}
            testID={`driving-limit-mode-${option}`}
            accessibilityRole="radio"
            accessibilityState={{ checked, disabled }}
            accessibilityLabel={t(`workTime.mode.${key}.title`)}
            disabled={disabled}
            onPress={() => {
              if (!checked) onChange(option);
            }}
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 8,
              minHeight: 48,
              padding: 12,
              borderRadius: 12,
              borderWidth: checked ? 2 : 1,
              borderColor: checked ? theme.colors.primary : theme.colors.outlineVariant,
            }}
          >
            <View pointerEvents="none">
              <RadioButton.Android value={option} status={checked ? 'checked' : 'unchecked'} disabled={disabled} />
            </View>
            <View style={{ flex: 1, gap: 2 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Text variant="titleSmall" style={{ color: theme.colors.onSurface }}>
                  {t(`workTime.mode.${key}.title`)}
                </Text>
                {option === 'rolling_24h' ? (
                  <Text
                    testID="driving-limit-mode-recommended"
                    variant="labelSmall"
                    style={{
                      color: theme.colors.onPrimaryContainer,
                      backgroundColor: theme.colors.primaryContainer,
                      paddingHorizontal: 8,
                      paddingVertical: 2,
                      borderRadius: 999,
                      overflow: 'hidden',
                    }}
                  >
                    {t('workTime.mode.recommended')}
                  </Text>
                ) : null}
              </View>
              <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                {t(`workTime.mode.${key}.description`)}
              </Text>
            </View>
          </Pressable>
        );
      })}
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
        {t('workTime.mode.nothingChanges')}
      </Text>
    </View>
  );
};
